import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useAvailableAutopricingModes } from 'src/hooks/useAvailableAutopricingModes';
import { WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { getCompleteEventConfigScoreOverrides } from 'src/utils/seatScoreUtils';
import { ListingDetailsAutoPricingSectionUpdatesV2 } from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingSettings } from '../useBulkAutoPricingSettings';
import { AutoPricingEnabledSetting } from './AutoPricingEnabled';
import * as styles from './AutoPricingSettings.css';
import { PricingAdvancedSettings } from './PricingAdvancedSettings';
import { PricingAgainst } from './PricingAgainst';
import { PricingProtection } from './PricingProtection';
import { PricingSetting } from './PricingSetting';
import { AutoPricingStrategySetting } from './PricingStrategy';
import { PricingUndercutting } from './PricingUndercutting/PricingUndercutting';

type AutoPricingSettingsProps = {
  settingsInput: AutoPricingSettingsInput;
  canUseZoneMap?: boolean;
};

export const AutoPricingSettings: React.FC<AutoPricingSettingsProps> = ({
  settingsInput,
  canUseZoneMap,
}) => {
  const settingContent = useContent(ContentId.Settings);
  const details = useContent(ContentId.Details);
  const status = useContent(ContentId.Status);

  const {
    compListingMode,
    undercutMode,
    compListingOnlyForSelectedSectionsEnabled,
  } = useAutoPricingSettings();

  const { activeConfigOverride, venueMapsByScoreModelQuery } =
    useEventMapContext();

  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const deriveCompsFromVenueZoneConfig = watch(
    'deriveCompsFromVenueZoneConfig'
  );

  const defaultScoreOverrides = useMemo(() => {
    // In the backend, if AVG scores are missing, then we always fallback to MVG, even if AVG is explicitly selected
    return (
      venueMapsByScoreModelQuery?.data?.SqsClassifier?.sectionScores ??
      venueMapsByScoreModelQuery?.data?.MergedVenueGeometry?.sectionScores ??
      venueMapsByScoreModelQuery?.data?.VenueGeometry?.sectionScores
    );
  }, [
    venueMapsByScoreModelQuery?.data?.SqsClassifier?.sectionScores,
    venueMapsByScoreModelQuery?.data?.MergedVenueGeometry?.sectionScores,
    venueMapsByScoreModelQuery?.data?.VenueGeometry?.sectionScores,
  ]);

  const scores = getCompleteEventConfigScoreOverrides(
    defaultScoreOverrides,
    activeConfigOverride?.scoreOverrides,
    false
  );

  const {
    outlierModes,
    outlierModesDisabled,
    compListingModes,
    compListingModesDisabled,
  } = useAvailableAutopricingModes(
    true,
    undercutMode?.value,
    compListingMode?.value,
    Boolean(scores?.some((s) => s.score)),
    false,
    false,
    compListingOnlyForSelectedSectionsEnabled?.value,
    deriveCompsFromVenueZoneConfig
  );

  const hasConflict = useMemo(
    () => Object.values(settingsInput).some(({ hasConflict }) => hasConflict),
    [settingsInput]
  );

  return (
    <Stack direction="column" gap="s" alignItems="start" width="full">
      {hasConflict && (
        <Stack
          width="full"
          direction="row"
          gap="m"
          className={styles.conflictAlert}
        >
          <WarningOutlineIcon size={vars.iconSize.l} />
          <Content id={ContentId.PricingSettingsListingsConflictWarning} />
        </Stack>
      )}
      <PricingSetting
        header={settingContent}
        detail={details}
        status={status}
        isHeader
      />
      <AutoPricingStrategySetting />
      <AutoPricingEnabledSetting origin={settingsInput} />
      <PricingAgainst
        origin={settingsInput}
        compListingModes={compListingModes}
        compListingModesDisabled={compListingModesDisabled}
        canUseZoneMap={canUseZoneMap}
      />
      <PricingProtection origin={settingsInput} />
      <PricingUndercutting origin={settingsInput} disabled={false} />
      <PricingAdvancedSettings
        origin={settingsInput}
        outlierModes={outlierModes}
        outlierModesDisabled={outlierModesDisabled}
      />
    </Stack>
  );
};
